<!--
A grid composed of a sidebar and a content area.
Top and bottom content are resizable.
Bottom content has a scrollbar.

+----------------------+----------------------+
| sidebar title        | content title        |
+----------------------+----------------------+
| sidebar              | content top          |
+                      +--------- <=> --------+
|                      | content bottom       |
+----------------------+----------------------+
-->

<script setup lang="ts">
import Simplebar from "simplebar-vue";
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";

const { topMinHeight, localStorageKey = "grid-layout-top-height" } = defineProps<{
  topMinHeight?: number;
  localStorageKey?: string;
}>();

const topHeight = ref(50); // percentage of available space (0-100)
const isResizing = ref(false);
const container = useTemplateRef<HTMLDivElement>("container");
const resizeHandle = useTemplateRef<HTMLDivElement>("resizeHandle");

let startY = 0;
let startTopHeight = 0;
let resizeObserver: ResizeObserver | null = null;

// Convert percentage to fr units for grid-template-rows
const gridTemplateRows = computed(() => {
  const topFr = topHeight.value;
  const bottomFr = 100 - topHeight.value;
  return `auto ${topFr}fr auto ${bottomFr}fr`;
});

function beginResize(clientY: number, type: "mouse" | "touch") {
  isResizing.value = true;
  startY = clientY;
  startTopHeight = topHeight.value;

  if (type === "mouse") {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  } else {
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);
    document.addEventListener("touchcancel", handleTouchEnd);
  }
}

function handleMouseDown(e: MouseEvent) {
  e.preventDefault();
  beginResize(e.clientY, "mouse");
}

function handleTouchStart(e: TouchEvent) {
  if (e.touches.length !== 1) return;
  e.preventDefault();
  const touch = e.touches[0];
  if (!touch) return;
  beginResize(touch.clientY, "touch");
}

function applyResize(clientY: number) {
  if (!container.value) return;

  const containerRect = container.value.getBoundingClientRect();
  // Get the title row height (first row)
  const titleRow = container.value.querySelector(".title");
  const titleHeight = titleRow?.getBoundingClientRect().height || 0;

  const availableHeight = containerRect.height - titleHeight;
  const handleHeight = resizeHandle.value?.offsetHeight ?? 8;
  const usableHeight = availableHeight - handleHeight;

  // Calculate minimum percentage if topMinHeight is set
  let minPercent = 10; // Default minimum
  if (topMinHeight !== undefined && usableHeight > 0) {
    const minPercentFromPixels = (topMinHeight / usableHeight) * 100;
    minPercent = Math.max(minPercent, minPercentFromPixels);
  }

  const deltaY = clientY - startY;
  const deltaPercent = (deltaY / usableHeight) * 100;

  const newTopHeight = Math.max(minPercent, Math.min(90, startTopHeight + deltaPercent));
  topHeight.value = newTopHeight;
}

function handleMouseMove(e: MouseEvent) {
  if (!isResizing.value) return;
  applyResize(e.clientY);
}

function handleTouchMove(e: TouchEvent) {
  if (!isResizing.value) return;
  const touch = e.touches[0];
  if (!touch) return;
  e.preventDefault();
  applyResize(touch.clientY);
}

function endResize(type: "mouse" | "touch") {
  isResizing.value = false;
  if (type === "mouse") {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  } else {
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);
    document.removeEventListener("touchcancel", handleTouchEnd);
  }
  localStorage.setItem(localStorageKey, topHeight.value.toString());
}

function handleMouseUp() {
  endResize("mouse");
}

function handleTouchEnd() {
  endResize("touch");
}

function enforceMinHeight() {
  if (topMinHeight === undefined || !container.value) return;

  const containerRect = container.value.getBoundingClientRect();
  const titleRow = container.value.querySelector(".title");
  const titleHeight = titleRow?.getBoundingClientRect().height || 0;
  const availableHeight = containerRect.height - titleHeight;
  const handleHeight = resizeHandle.value?.offsetHeight ?? 8;
  const usableHeight = availableHeight - handleHeight;

  if (usableHeight > 0) {
    const minPercent = (topMinHeight / usableHeight) * 100;
    if (topHeight.value < minPercent) {
      topHeight.value = Math.min(minPercent, 90);
    }
  }
}

onMounted(() => {
  // Load saved resize state from localStorage if available
  const saved = localStorage.getItem(localStorageKey);
  if (saved) {
    const parsed = parseFloat(saved);
    if (!isNaN(parsed) && parsed >= 10 && parsed <= 90) {
      topHeight.value = parsed;
    }
  }

  // Ensure initial height respects topMinHeight if set
  enforceMinHeight();

  // Watch for container resize to enforce minimum height
  if (topMinHeight !== undefined && container.value) {
    resizeObserver = new ResizeObserver(() => {
      enforceMinHeight();
    });
    resizeObserver.observe(container.value);
  }
});

onBeforeUnmount(() => {
  // Save resize state to localStorage
  if (isResizing.value) {
    // If a resize is in progress, ensure listeners are cleared
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);
    document.removeEventListener("touchcancel", handleTouchEnd);
  }
  localStorage.setItem(localStorageKey, topHeight.value.toString());

  // Clean up ResizeObserver
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});
</script>

<template>
  <div
    ref="container"
    class="grid-layout-with-sidebar"
    :class="{ resizing: isResizing }"
    :style="{ gridTemplateRows: gridTemplateRows }"
  >
    <div v-if="$slots.sidebarTitle" class="title sidebar">
      <slot name="sidebarTitle"></slot>
    </div>

    <div v-if="$slots.contentTitle" class="title content">
      <slot name="contentTitle"></slot>
    </div>

    <div class="content sidebar">
      <Simplebar class="scroller">
        <slot name="sidebar"></slot>
      </Simplebar>
    </div>

    <div class="content top">
      <slot name="top"></slot>
    </div>

    <div
      ref="resizeHandle"
      class="resize-handle"
      :class="{ resizing: isResizing }"
      @mousedown="handleMouseDown"
      @touchstart="handleTouchStart"
    >
      <div class="resize-handle-line"></div>
    </div>

    <div class="content bottom">
      <Simplebar class="scroller">
        <slot name="bottom"></slot>
      </Simplebar>
    </div>
  </div>
</template>

<style scoped>
.grid-layout-with-sidebar {
  --radius: var(--numbers-12);

  display: grid;
  overflow: hidden;
  min-width: 0;
  height: 100%;
  border: solid var(--stroke-width-md) var(--border-tertiary);
  border-radius: var(--radius);
  grid-template-columns: 20% 1fr;

  .title {
    padding: var(--numbers-6) var(--numbers-24);
    border-bottom: solid var(--stroke-width-md) var(--border-tertiary);
    background-color: var(--background-tertiary);
    grid-row: 1 / 2;
  }

  .title.sidebar {
    border-right: solid var(--stroke-width-md) var(--border-tertiary);
    grid-column: 1 / 2;
  }

  .content.sidebar {
    overflow: hidden;
    min-height: 0;
    border-right: solid var(--stroke-width-md) var(--border-tertiary);
    background-color: var(--background-primary);
    border-bottom-left-radius: var(--radius);
    grid-column: 1 / 2;
    grid-row: 2 / 5;

    :deep(.simplebar-mask) {
      border-bottom-left-radius: var(--radius);
      border-top-left-radius: var(--radius);
    }
  }

  .content.top {
    overflow: hidden;
    min-height: 0;
    grid-row: 2 / 3;
  }

  .resize-handle {
    position: relative;
    z-index: 1;
    display: flex;
    min-height: 20px;
    align-items: center;
    justify-content: center;
    border-top: solid var(--stroke-width-md) var(--border-tertiary);
    border-bottom: solid var(--stroke-width-md) var(--border-tertiary);
    background-color: var(--background-tertiary);
    cursor: row-resize;
    grid-column: 2 / 3;
    grid-row: 3 / 4;
    transition: background-color var(--animation-duration) var(--animation-easing);
    user-select: none;

    .resize-handle-line {
      width: var(--numbers-56);
      height: var(--numbers-4);
      border-radius: var(--numbers-4);
      background-color: var(--border-tertiary);
      transition:
        background-color var(--animation-duration) var(--animation-easing),
        width var(--animation-duration) var(--animation-easing);
    }

    &:hover .resize-handle-line,
    &.resizing .resize-handle-line {
      background-color: var(--border-secondary);
    }

    &.resizing .resize-handle-line {
      width: var(--numbers-72);
    }
  }

  .content.bottom {
    overflow: hidden;
    min-height: 0;
    border-bottom-right-radius: var(--radius);
    grid-row: 4 / 5;

    :deep(.simplebar-mask) {
      border-bottom-right-radius: var(--radius);
    }

    :deep(.simplebar-track.simplebar-vertical) {
      bottom: calc(var(--radius) * 0.55);
    }
  }

  .title.content {
    grid-column: 2 / 3;
  }

  .content.top,
  .content.bottom {
    background-color: var(--background-primary);
    grid-column: 2 / 3;
  }

  &.resizing {
    .content.top,
    .content.bottom {
      pointer-events: none;
    }
  }

  .scroller {
    max-width: 100%;
    max-height: 100%;
  }
}
</style>
